import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import type { ResultColumn, ResultEnvelope, Row, Visualization } from "@cd/core";
import {
  canPick, formatTimeBucket, formatValue, gradientDomain, gradientFill, humanize, isGradientScheme, isRowDimmed, type GradientDomain, type Highlight,
} from "@cd/viz";
import { useResolvedTheme } from "../../lib/theme";
import { cn } from "../../lib/utils";
import type { OnPick } from "./onPick";

const cell = (c: ResultColumn, v: Row[string] | undefined) =>
  c.kind === "metric" ? formatValue(v, c.format) : c.type === "time" ? formatTimeBucket(v, c.grain) : humanize(v);

export function DataTable({ viz, result, onPick, highlight }: { viz: Visualization; result: ResultEnvelope; onPick?: OnPick; highlight?: Highlight[] }) {
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);
  const rows = useMemo(() => {
    if (!sort) return result.rows;
    return [...result.rows].sort((a, b) => {
      const x = a[sort.key] ?? null, y = b[sort.key] ?? null;
      if (x === y) return 0;
      if (x === null) return 1;
      if (y === null) return -1;
      return (x > y ? 1 : -1) * sort.dir;
    });
  }, [result.rows, sort]);

  const mode = useResolvedTheme();
  const scheme = isGradientScheme(viz.config.colorScale) ? viz.config.colorScale : "none";
  const reverse = viz.config.colorReverse === true;
  // A color scale replaces data bars: both at once is noise.
  const dataBars = viz.config.dataBars !== false && scheme === "none";
  const { maxByKey, domainByKey } = useMemo(() => {
    const maxByKey: Record<string, number> = {};
    const domainByKey: Record<string, GradientDomain | null> = {};
    for (const c of result.columns) {
      if (c.kind !== "metric") continue;
      maxByKey[c.key] = Math.max(0, ...result.rows.map((r) => (typeof r[c.key] === "number" ? (r[c.key] as number) : 0)));
      // Each metric column gets its own scale: sales and order counts aren't comparable.
      domainByKey[c.key] = gradientDomain(result.rows.map((r) => r[c.key]));
    }
    return { maxByKey, domainByKey };
  }, [result]);

  const showTotals = viz.config.totals !== false && rows.length > 1;

  return (
    <div className="size-full overflow-auto">
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead className="sticky top-0 z-10 bg-surface">
          <tr>
            {result.columns.map((c) => (
              <th
                key={c.key}
                onClick={() => setSort((s) => (s?.key === c.key ? { key: c.key, dir: s.dir === 1 ? -1 : 1 } : { key: c.key, dir: c.kind === "metric" ? -1 : 1 }))}
                className={cn(
                  "cursor-pointer select-none border-b border-border px-3 py-2 text-xs font-medium whitespace-nowrap text-muted hover:text-foreground",
                  c.kind === "metric" ? "text-right" : "text-left",
                )}
              >
                <span className="inline-flex items-center gap-1">
                  {c.label}
                  {sort?.key === c.key && (sort.dir === 1 ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />)}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={cn("hover:bg-surface-2/60", isRowDimmed(highlight, r) && "opacity-40")}>
              {result.columns.map((c) => {
                const v = r[c.key];
                const pct = c.kind === "metric" && dataBars && typeof v === "number" && maxByKey[c.key] ? (v / maxByKey[c.key]!) * 100 : 0;
                const pickable = onPick && canPick(c);
                return (
                  <td
                    key={c.key}
                    onClick={pickable ? (e) => onPick([{ column: c, value: v ?? null }], { x: e.clientX, y: e.clientY }) : undefined}
                    // Only numbers stay on one line; a long name off-screen would otherwise widen its column and push the last one out of view.
                    className={cn("relative border-b border-border px-3 py-1.5", c.kind === "metric" && "tabular text-right whitespace-nowrap")}
                    style={c.kind === "metric" ? { background: gradientFill(scheme, v, domainByKey[c.key] ?? null, mode, reverse) } : undefined}
                  >
                    {pct > 0 && (
                      <span className="absolute inset-y-1 right-1 rounded-sm bg-accent-soft" style={{ width: `calc(${pct}% - 8px)` }} aria-hidden />
                    )}
                    <span className={cn("relative", pickable && "cursor-pointer hover:underline")}>{cell(c, v)}</span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        {showTotals && (
          <tfoot className="sticky bottom-0 bg-surface">
            <tr>
              {result.columns.map((c, i) => (
                <td key={c.key} className={cn("border-t border-border-strong px-3 py-2 text-xs font-semibold", c.kind === "metric" && "tabular text-right")}>
                  {i === 0 ? (result.limit ? "Total (shown)" : "Total") : c.kind !== "metric" ? "" : c.additive ? formatValue(rows.reduce((a, r) => a + ((r[c.key] as number) ?? 0), 0), c.format) : (
                    <span className="font-normal text-subtle" title="This metric can't be summed across rows">—</span>
                  )}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
